import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UpdateBlobDocumentModel } from 'lemunzo-api';
import { extensions as mimeExtensions } from 'mime-types';
import { MessageService } from 'primeng/api';
import { filter, finalize, share } from 'rxjs';
import { MIMETypeEnum } from '../../enums/mime-types';
import { PrimeNGToastSeverity } from '../../enums/primeng-toast-severity';
import {
    FileUploadRequirementsConfig,
    ResolvedFileUploadLimits,
    resolveFileUploadLimits,
} from '../../models/file-upload-requirements-config';
import { UploadedFile } from '../../models/uploaded-file';
import { ConstantsService } from '../../services/constants.service';
import { FileUploadService } from '../../services/file-upload.service';
import { formatFileSize } from '../../utils/formatFileSize';
import { FilesTableComponent } from '../files-table/files-table.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TooltipCustomComponent } from '../tooltip-custom/tooltip-custom.component';

@Component({
    selector: 'apcam-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FilesTableComponent,
        LoadingSpinnerComponent,
        MatButtonModule,
        MatIconModule,
        TooltipCustomComponent,
        TranslatePipe,
    ],
})
export class FileUploadComponent {
    readonly allowedMIMETypes = input<MIMETypeEnum[]>([]);
    readonly disabled = input(false);
    readonly hideSubmitButton = input(false);
    readonly isOptional = input(false);
    readonly isSubmitting = input(false);
    readonly multiple = input(false);
    readonly showRequirements = input(false);
    readonly requirementsConfig = input<
        Partial<FileUploadRequirementsConfig> | undefined
    >(undefined);
    /**
     * Parent-driven submit request. Increment (e.g. `trigger.update((n) => n + 1)`)
     * when the parent wants `filesEvent` emitted. Default `0` = never requested.
     * A bump while uploads are in-flight is ignored (not deferred).
     *
     * Parents that read `filesEvent` side effects in the same synchronous turn must
     * flush child updates (e.g. `ChangeDetectorRef.detectChanges()`) after bumping,
     * because signal input bindings do not propagate as synchronously as `Subject.next()`.
     * For same-tick imperative submit, call public {@link submit()} via `ViewChild` instead.
     */
    readonly submitTrigger = input(0);
    readonly uploadWorkerUrl = input.required<string>();
    readonly files = model<UploadedFile[]>([]);
    readonly allFilesUploaded = output<boolean>();
    readonly filesEvent = output<UpdateBlobDocumentModel[]>();

    protected readonly dragOver = signal(false);
    protected readonly filesUploading = new WeakMap<UploadedFile, boolean>();

    /**
     * Tracks in-flight deletes keyed by {@link UploadedFile} reference (stable across
     * array index shifts). Mutated in place — Angular does not detect WeakMap changes.
     */
    protected readonly filesDeleting = new WeakMap<UploadedFile, boolean>();

    protected readonly allFilesUploadedInternal = signal(false);

    /**
     * Change-detection revision counter for delete UI. Bumped whenever {@link filesDeleting}
     * is updated so OnPush + {@link FilesTableComponent} re-render after WeakMap mutations.
     * The numeric value is meaningless; only increments matter. Bound on
     * {@link FilesTableComponent} as `deleteUiRevision` input — do not use for business logic.
     */
    protected readonly deleteUiRevision = signal(0);

    private readonly langRevision = signal(0);
    private prevSubmitTrigger = 0;
    private lastEmittedUploadState: boolean | undefined;
    private lastEmittedFilesCount: number | undefined;

    /** 1 when `multiple` is false; otherwise `requirementsConfig.maxFileCount` (default 10). */
    protected readonly effectiveMaxFileCount = computed(() => {
        if (!this.multiple()) {
            return 1;
        }
        return this.resolvedLimits().maxFileCount;
    });

    protected readonly effectiveMaxFileSizeBytes = computed(
        () => this.resolvedLimits().maxFileSizeBytes
    );

    protected readonly acceptedFormats = computed(() =>
        this.allowedMIMETypes().join(',')
    );

    protected readonly canDropNextFile = computed(
        () =>
            !this.disabled() &&
            this.files().length < this.effectiveMaxFileCount()
    );

    protected readonly isUploadInProgress = computed(
        () => this.files().length > 0 && !this.allFilesUploadedInternal()
    );

    protected readonly canSubmit = computed(
        () =>
            !this.disabled() &&
            this.allFilesUploadedInternal() &&
            !this.isSubmitting()
    );

    protected readonly showSubmitSpinner = computed(
        () => this.isUploadInProgress() || this.isSubmitting()
    );

    protected readonly canClearAllFiles = computed(
        () => this.files().length > 0 && this.allFilesUploadedInternal()
    );

    protected readonly requirementItems = computed(() => {
        this.langRevision();

        if (!this.showRequirements()) {
            return [];
        }

        const items: string[] = [];
        const limits = this.resolvedLimits();

        if (this.allowedMIMETypes().length > 0) {
            const types = [
                ...new Set(
                    this.getValidFileExtensions().map((ext) =>
                        ext.toUpperCase()
                    )
                ),
            ].join(', ');

            items.push(
                this.translateService.instant(
                    'shared.fileUploadDragDrop.requirements.allowedTypes',
                    { types }
                )
            );
        } else {
            items.push(
                this.translateService.instant(
                    'shared.fileUploadDragDrop.requirements.allTypes'
                )
            );
        }

        items.push(
            this.translateService.instant(
                'shared.fileUploadDragDrop.requirements.maxFileSize',
                {
                    size: formatFileSize(limits.maxFileSizeBytes, 2),
                }
            )
        );

        const maxCount = this.effectiveMaxFileCount();

        items.push(
            this.translateService.instant(
                'shared.fileUploadDragDrop.requirements.maxFileCount',
                { count: maxCount }
            )
        );

        items.push(...(this.requirementsConfig()?.additionalItems ?? []));

        return items;
    });

    protected readonly uploadButtonLabel = computed(() => {
        this.langRevision();

        const count = this.files().length;

        if (count === 0) {
            return this.translateService.instant(
                'shared.fileUploadDragDrop.upload'
            );
        }

        return this.translateService.instant(
            'shared.fileUploadDragDrop.uploadFiles',
            { count }
        );
    });

    protected readonly submitButtonAriaLabel = computed(() => {
        this.langRevision();

        const count = this.files().length;

        if (this.isSubmitting()) {
            return this.translateService.instant(
                'shared.fileUploadDragDrop.submitBusyAriaLabel'
            );
        }

        if (this.isUploadInProgress()) {
            return this.translateService.instant(
                'shared.fileUploadDragDrop.uploadBusyAriaLabel',
                { count }
            );
        }

        if (count > 0) {
            return this.translateService.instant(
                'shared.fileUploadDragDrop.uploadFiles',
                { count }
            );
        }

        return this.translateService.instant(
            'shared.fileUploadDragDrop.uploadVerbose'
        );
    });

    protected readonly submitButtonAriaBusy = computed((): boolean | null =>
        this.isUploadInProgress() || this.isSubmitting() ? true : null
    );

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly fileUploadService: FileUploadService,
        private readonly toastService: MessageService,
        private readonly translateService: TranslateService
    ) {
        effect(() => {
            this.syncAllFilesUploadedInternal(this.files());
        });

        effect(() => {
            const value = this.allFilesUploadedInternal();
            const filesCount = this.files().length;

            if (
                value === this.lastEmittedUploadState &&
                filesCount === this.lastEmittedFilesCount
            ) {
                return;
            }

            this.emitAllFilesUploadedState();
        });

        afterNextRender(() => {
            this.emitAllFilesUploadedState();
        });

        effect(() => {
            const trigger = this.submitTrigger();
            if (trigger === this.prevSubmitTrigger) {
                return;
            }

            this.prevSubmitTrigger = trigger;
            if (trigger === 0) {
                return;
            }

            if (!this.allFilesUploadedInternal()) {
                return;
            }

            this.submit();
        });

        this.translateService.onLangChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.langRevision.update((n) => n + 1));
    }

    protected upload(
        eventTarget: EventTarget | DataTransfer | null,
        fileInput?: HTMLInputElement
    ): void {
        if (eventTarget == null) return;
        if (!this.canDropNextFile()) return;

        const files = (eventTarget as HTMLInputElement).files;

        if (files == null || files.length === 0) {
            return;
        }

        const incoming = Array.from(files).filter(
            (file): file is File => file != null
        );

        if (
            this.files().length + incoming.length >
            this.effectiveMaxFileCount()
        ) {
            this.handleMaxFileCountExceeded();
            this.resetFileInput(fileInput);
            return;
        }

        if (
            incoming.some(
                (file) => file.size > this.effectiveMaxFileSizeBytes()
            )
        ) {
            this.handleMaxFileSizeExceeded();
            this.resetFileInput(fileInput);
            return;
        }

        for (const currFile of incoming) {
            if (!this.isValidMimeType(currFile)) {
                this.handleInvalidFileType(currFile.name);
                continue;
            }

            const tmp = this.fileUploadService.uploadFile(
                currFile,
                this.uploadWorkerUrl()
            );

            const newFile: UploadedFile = {
                file: currFile,
                percentage: tmp.percentage.pipe(share()),
                blobName: tmp.blobName,
                hidden: false,
                virtual: false,
            };

            this.monitorFileUploadProcess(newFile);

            this.files.update((prev) => {
                return [...prev, newFile];
            });
        }

        this.resetFileInput(fileInput);
    }

    private resolvedLimits(): ResolvedFileUploadLimits {
        return resolveFileUploadLimits(this.requirementsConfig());
    }

    private isValidMimeType(file: File): boolean {
        if (this.allowedMIMETypes().length === 0) {
            return true;
        }

        if (this.allowedMIMETypes().includes(file.type as MIMETypeEnum)) {
            return true;
        }

        // CSV corner case: CSV file reported as Excel MIME
        // https://planetjon.ca/uploading-csv-unexpected-mime-5796
        return (
            this.allowedMIMETypes().includes(MIMETypeEnum.TEXT_CSV) &&
            file.type === MIMETypeEnum.APPLICATION_VND_MS_EXCEL &&
            file.name.toLowerCase().endsWith('.csv')
        );
    }

    private resetFileInput(fileInput?: HTMLInputElement): void {
        if (fileInput != null) {
            fileInput.value = '';
        }
    }

    private handleMaxFileCountExceeded(): void {
        this.toastService.add({
            key: ConstantsService.TOAST_KEY,
            severity: PrimeNGToastSeverity.ERROR,
            summary: this.translateService.instant(
                'shared.fileUploadDragDrop.maxFileCountExceeded.summary'
            ),
            detail: this.translateService.instant(
                'shared.fileUploadDragDrop.maxFileCountExceeded.detail',
                { count: this.effectiveMaxFileCount() }
            ),
        });
    }

    private handleMaxFileSizeExceeded(): void {
        this.toastService.add({
            key: ConstantsService.TOAST_KEY,
            severity: PrimeNGToastSeverity.ERROR,
            summary: this.translateService.instant(
                'shared.fileUploadDragDrop.maxFileSizeExceeded.summary'
            ),
            detail: this.translateService.instant(
                'shared.fileUploadDragDrop.maxFileSizeExceeded.detail',
                {
                    size: formatFileSize(this.effectiveMaxFileSizeBytes(), 2),
                }
            ),
        });
    }

    private handleInvalidFileType(fileName: string): void {
        const validFileExtensions = this.getValidFileExtensions();
        const EXTENSIONS_JOIN_STRING = ', .';
        const validFileExtensionsAsString =
            '.' + validFileExtensions.join(EXTENSIONS_JOIN_STRING);

        this.toastService.add({
            key: ConstantsService.TOAST_KEY,
            severity: PrimeNGToastSeverity.ERROR,
            summary: this.translateService.instant(
                'shared.fileUploadDragDrop.invalidFileTypeToast.summary'
            ),
            detail:
                this.translateService.instant(
                    'shared.fileUploadDragDrop.invalidFileTypeToast.detail.1'
                ) +
                ` '${fileName}'. ` +
                this.translateService.instant(
                    'shared.fileUploadDragDrop.invalidFileTypeToast.detail.2'
                ) +
                this.translateService.instant(
                    'shared.fileUploadDragDrop.invalidFileTypeToast.detail.3'
                ) +
                validFileExtensionsAsString,
        });
    }

    private getValidFileExtensions(): string[] {
        const validFileExtensions: string[] = [];

        this.allowedMIMETypes().forEach((type) => {
            const extensionsPerMIMEType = mimeExtensions[type];

            if (extensionsPerMIMEType) {
                extensionsPerMIMEType.forEach((extension) => {
                    validFileExtensions.push(extension);
                });
            }
        });

        return validFileExtensions;
    }

    private monitorFileUploadProcess(file: UploadedFile): void {
        this.allFilesUploadedInternal.set(false);
        this.filesUploading.set(file, true);

        file.percentage
            .pipe(
                filter((result) => result === 100 || result === -1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (value) => {
                    if (value === -1) {
                        file.hidden = true;
                        this.files.set(this.files().filter((f) => !f.hidden));
                    }

                    this.filesUploading.delete(file);
                    this.allFilesUploadedInternal.set(
                        !this.files().some(
                            (f) => this.filesUploading.get(f) === true
                        )
                    );
                },
            });
    }

    private syncAllFilesUploadedInternal(files: UploadedFile[]): void {
        if (files.some((f) => this.filesUploading.get(f))) {
            return;
        }

        if (files.length === 0) {
            this.allFilesUploadedInternal.set(this.isOptional());
            return;
        }

        this.allFilesUploadedInternal.set(true);
    }

    private emitAllFilesUploadedState(): void {
        const value = this.allFilesUploadedInternal();
        const filesCount = this.files().length;
        this.lastEmittedUploadState = value;
        this.lastEmittedFilesCount = filesCount;
        this.allFilesUploaded.emit(value);
    }

    protected removeFile(index: number): void {
        if (this.files().length <= index) {
            return;
        }

        const file = this.files()[index];

        this.filesDeleting.set(file, true);
        this.bumpDeleteUiRevision();

        if (file.virtual) {
            this.removeFileFromList(index);
            this.filesDeleting.delete(file);
            this.bumpDeleteUiRevision();
            return;
        }

        const blobName = file.blobName.getValue();

        if (blobName == null) {
            this.filesDeleting.delete(file);
            this.bumpDeleteUiRevision();
            return;
        }

        this.fileUploadService
            .deleteFile(blobName, this.uploadWorkerUrl())
            .pipe(
                finalize(() => {
                    this.filesDeleting.delete(file);
                    this.bumpDeleteUiRevision();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (_) => {
                    this.removeFileFromList(this.files().indexOf(file));
                },
            });
    }

    private removeFileFromList(index: number) {
        this.files.update((prev) => prev.filter((_file, i) => i !== index));

        if (this.files().length <= 0 && !this.isOptional()) {
            this.allFilesUploadedInternal.set(false);
        }
    }

    /** Notify child table that {@link filesDeleting} changed (WeakMap is not signal-tracked). */
    private bumpDeleteUiRevision(): void {
        this.deleteUiRevision.update((n) => n + 1);
    }

    public submit(): void {
        if (!this.canSubmit()) {
            return;
        }

        this.filesEvent.emit(this.fileInternalToUpdateModel());
    }

    protected clearAllFiles(): void {
        if (!this.canClearAllFiles()) {
            return;
        }

        if (this.files().some((f) => this.filesDeleting.get(f) === true)) {
            return;
        }

        [...this.files().keys()].reverse().forEach((index) => {
            this.removeFile(index);
        });
    }

    protected onDragOver(): void {
        this.dragOver.set(this.canDropNextFile());
    }

    protected onDragLeave(): void {
        this.dragOver.set(false);
    }

    protected onDrop(e: Event): void {
        if (!this.canDropNextFile()) return;

        this.upload((e as DragEvent).dataTransfer);
        this.dragOver.set(false);
    }

    private fileInternalToUpdateModel(): UpdateBlobDocumentModel[] {
        return this.files()
            .filter((x) => x.blobName.value != null)
            .map((x) => {
                return {
                    documentType: x.file.type,
                    documentName: x.file.name,
                    tmpBlobName: x.blobName.value!,
                };
            });
    }
}
